import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MathBackground } from './MathBackground';
import { useLanguage } from '../lib/LanguageContext';
import { translations } from '../lib/translations';
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
};
export function HeroSection() {
  const {
    lang
  } = useLanguage();
  const t = translations[lang].hero;
  return <section style={{
    position: 'relative',
    minHeight: '100vh',
    backgroundColor: 'var(--black)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  }} data-id="element-36">
      <MathBackground data-id="element-37" />

      <motion.div variants={containerVariants} initial="hidden" animate="visible" style={{
      position: 'relative',
      zIndex: 1,
      maxWidth: '80rem',
      margin: '0 auto',
      padding: '0 2rem',
      width: '100%'
    }} data-id="element-38">
        {/* Top label */}
        <motion.p variants={itemVariants} style={{
        fontFamily: '"IBM Plex Mono", monospace',
        fontSize: '0.75rem',
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        color: 'var(--grey-500)',
        marginBottom: '3rem'
      }} data-id="element-39">
          {t.label}
        </motion.p>

        {/* Main headline */}
        <motion.div variants={itemVariants} data-id="element-40">
          <h1 style={{
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 900,
          lineHeight: 1,
          letterSpacing: '-0.02em',
          color: 'var(--white)',
          fontSize: 'clamp(48px, 8vw, 120px)',
          margin: 0
        }} data-id="element-41">
            {t.line1}
          </h1>
          <h1 style={{
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 900,
          lineHeight: 1,
          letterSpacing: '-0.02em',
          color: 'var(--white)',
          fontSize: 'clamp(48px, 8vw, 120px)',
          margin: 0
        }} data-id="element-42">
            {t.line2}
          </h1>
        </motion.div>

        {/* Thin rule */}
        <motion.hr variants={itemVariants} style={{
        border: 'none',
        borderTop: '1px solid var(--grey-700)',
        width: '6rem',
        margin: '2rem 0'
      }} data-id="element-43" />

        {/* Subheadline */}
        <motion.p variants={itemVariants} style={{
        fontFamily: '"IBM Plex Sans", sans-serif',
        fontWeight: 300,
        fontSize: 'clamp(1.1rem, 2vw, 1.5rem)',
        color: 'var(--grey-300)',
        lineHeight: 1.6,
        maxWidth: '32rem',
        margin: 0
      }} data-id="element-44">
          {t.sub1}
          <br data-id="element-45" />
          {t.sub2}
        </motion.p>

        {/* Emphasis line */}
        <motion.div variants={itemVariants} style={{
        margin: '3rem 0'
      }} data-id="element-46">
          <div style={{
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 900,
          lineHeight: 1,
          color: 'var(--white)',
          fontSize: 'clamp(64px, 10vw, 160px)',
          letterSpacing: '-0.02em'
        }} data-id="element-47">
            {t.emphasis}
          </div>
          <div style={{
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.875rem',
          letterSpacing: '0.4em',
          color: 'var(--grey-500)',
          marginTop: '0.5rem'
        }} data-id="element-48">
            {t.emphasisSub}
          </div>
        </motion.div>

        {/* CTA Button */}
        <motion.div variants={itemVariants} data-id="element-49">
          <Link to="/why-white-box" style={{
          textDecoration: 'none'
        }} data-id="element-50">
            <button style={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 300,
            fontSize: '0.875rem',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: 'var(--white)',
            backgroundColor: 'transparent',
            border: '1px solid var(--white)',
            borderRadius: 0,
            padding: '1rem 2rem',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease, color 0.2s ease'
          }} onMouseEnter={e => {
            ;
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--white)';
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--black)';
          }} onMouseLeave={e => {
            ;
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--white)';
          }} data-id="element-51">
              {t.cta}
            </button>
          </Link>
        </motion.div>
      </motion.div>

      {/* Bottom arrow */}
      <motion.div animate={{
      y: [0, 6, 0]
    }} transition={{
      repeat: Infinity,
      duration: 2.5,
      ease: 'easeInOut'
    }} style={{
      position: 'absolute',
      bottom: '2rem',
      left: '50%',
      transform: 'translateX(-50%)',
      color: 'var(--grey-500)',
      fontSize: '1.25rem',
      zIndex: 1
    }} data-id="element-52">
        ↓
      </motion.div>
    </section>;
}